import * as React from 'react';

import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import type { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import ComponentsConfigContext from 'bundles/page-config-common/contexts/ComponentsConfigContext';
import SandboxDataProvider from 'bundles/page-config-common/providers/SandboxDataProvider';
import { PageConfigFallbackData } from 'bundles/page-config-common/queries/mock-data/PageConfigComponentsFallbackData';
import { PageConfigMockData } from 'bundles/page-config-common/queries/mock-data/PageConfigComponentsMockData';
import PageConfigComponentsQuery from 'bundles/page-config-common/queries/page-config/PageConfigComponentsQuery.graphql';
import type {
  PageConfigComponentsQuery as PageConfigComponentsQueryType,
  PageConfigComponentsQueryVariables,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { getDropdownMenuProps } from 'bundles/unified-common/utils/pathwaysFilters';

type Props = {
  children: React.ReactNode;
  pageName: PageName;
  limit?: number;
};

const ComponentsConfigProvider = ({ children, pageName, limit = 25 }: Props) => {
  const showSandboxMode = GrowthDiscoveryExperiments.get('showUnifiedHomeSandbox');
  const { programSlug } = useParams();
  const { data, error, loading, refetch, fetchMore, networkStatus } = useQuery<
    PageConfigComponentsQueryType,
    PageConfigComponentsQueryVariables
  >(PageConfigComponentsQuery, {
    variables: {
      pageName,
      programSlug,
      limit,
      additionalInput: {
        level: getDropdownMenuProps().defaultValue,
      },
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: showSandboxMode,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // In Sandbox Mode, render mock data and execute mock data providers before moving them into the productionized HomePageProvider.
  if (showSandboxMode && !!PageConfigMockData[pageName]) {
    return (
      <ComponentsConfigContext.Provider
        value={{
          components: PageConfigMockData[pageName],
          refetch: () => '',
        }}
      >
        <SandboxDataProvider>{children}</SandboxDataProvider>
      </ComponentsConfigContext.Provider>
    );
  }

  // If there is an error fetching data or no components returned, render the fallback data.
  if (!loading && !data?.PageConfig?.queryPaginatedComponents?.elements) {
    captureException(error, {
      extra: { message: 'Error fetching page config components data' },
    });

    return (
      <ComponentsConfigContext.Provider
        value={{
          components: PageConfigFallbackData[pageName],
          refetch: () => '',
        }}
      >
        {children}
      </ComponentsConfigContext.Provider>
    );
  }

  return (
    <ComponentsConfigContext.Provider
      value={{
        components: data?.PageConfig?.queryPaginatedComponents?.elements,
        pagination: {
          cursor: data?.PageConfig?.queryPaginatedComponents?.pagination?.cursor,
          componentListMetadataHash: data?.PageConfig?.queryPaginatedComponents?.componentListMetadataHash,
        },
        refetch,
        fetchMore,
        loading: {
          isInitiallyLoading: networkStatus === 1,
          isFetchMoreLoading: networkStatus === 3,
        },
      }}
    >
      {children}
    </ComponentsConfigContext.Provider>
  );
};

export default ComponentsConfigProvider;
